/* Progressive orchestration of the native Studio. Diagnostics remain advisory. */
(() => {
  'use strict';
  if (window.TodayMerchPersonalizationOrchestrator) return;
  const informationCards = new WeakMap();
  const instances = new WeakMap(), unloaded = new WeakSet(), mountedRoots = new Set();
  const preparationLoads = new WeakMap(), preparationListeners = new WeakMap(), preparationRequested = new WeakSet(),
    preparationReady = new WeakSet(), preparationFailures = new WeakSet(), pendingApis = new WeakMap(), waitingRoots = new Set();
  let dossierFactory = null;
  const events = ['todaymerch:marking-state-change', 'todaymerch:marking-zone-change',
    'todaymerch:product-design-transform-change', 'todaymerch:artwork-production-source-change',
    'todaymerch:product-design-artwork-render', 'todaymerch:personalization-selection-change',
    'todaymerch:variant-media-updated', 'quantity-pricing:change', 'todaymerch:xd-quote-change',
    'todaymerch:production-priority-change', 'todaymerch:product-design-measurements-change',
    'todaymerch:artwork-quote-change', 'todaymerch:product-design-clear'];
  const fail = code => Object.assign(new Error(code), { code });
  const hash = async file => [...new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()))]
    .map(value => value.toString(16).padStart(2, '0')).join('');

  const reviewReminder = 'Notre équipe vérifie chaque détail de votre design pour un rendu final impeccable.';
  const reviewTitle = 'Notre équipe vérifiera votre design';
  function ensureInformationCard(root, selector) {
    const existing = informationCards.get(root);
    if (existing) { existing.place(); return existing; }
    const node = (tag, className, role) => { const element = document.createElement(tag); if (className) element.className = className; if (role) element.dataset[role] = ''; return element; };
    const mount = node('div', '', 'personalizationOrchestrationMount');
    const notes = node('section', 'personalization-notes personalization-notes--reassurance', 'personalizationNotes');
    notes.setAttribute('role', 'note'); notes.setAttribute('aria-label', 'Vérification de votre design'); notes.dataset.diagnosticState = 'reassurance';
    const notesIcon = node('span', 'personalization-notes__icon'); notesIcon.setAttribute('aria-hidden', 'true');
    notesIcon.innerHTML = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="8.25" stroke="currentColor" stroke-width="1.5"/><circle cx="7" cy="8" r="1" fill="currentColor"/><circle cx="13" cy="8" r="1" fill="currentColor"/><path d="M6.5 11.5c.7 1.5 1.9 2.25 3.5 2.25s2.8-.75 3.5-2.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    const notesContent = node('div', 'personalization-notes__content', 'personalizationNotesContent');
    const title = node('h3', 'personalization-notes__title', 'personalizationDiagnosticTitle'); title.textContent = reviewTitle;
    const body = node('div', 'personalization-notes__body', 'personalizationDiagnosticBody'); body.textContent = reviewReminder;
    notesContent.append(title, body); notes.append(notesIcon, notesContent); mount.append(notes);
    // Preparation keeps its own editor. Its internal hints do not mutate the
    // reassurance card or insert loading states into the product layout.
    const preparationNotes = node('div');
    const place = () => {
      const cards = root.querySelector('[data-xd-technique-cards],[data-personalization-v2-cards],[data-personalization-technique-cards]');
      const next = cards?.closest('[data-role="studio-upload-zone"]') || cards?.closest('product-personalization-choice') || cards || selector;
      // The technique block can move after initialization without changing parent.
      if (next?.parentElement && next.nextElementSibling !== mount) next.insertAdjacentElement('afterend', mount);
    };
    const card = { mount, notes, notesContent, title, body, preparationNotes, place };
    informationCards.set(root, card); place(); return card;
  }

  function requestPreparation(root, selector, intent = false) {
    const modules = window.TodayMerchProductModules;
    if (!modules?.has?.(root, 'preparation')) return false;
    if (preparationReady.has(root)) return false;
    const hasArtwork = () => {
      const current = selector?.isConnected && root.contains(selector) ? selector : root.querySelector('product-marking-selector');
      return (current?.optionWraps || []).some(wrap => current.getLogoFiles?.(wrap)?.length)
        || [...(current?.querySelectorAll('[data-role="logo-file-input"]') || [])].some(input => input.files?.length);
    };
    const report = () => { if (root.isConnected) ensureInformationCard(root, selector); };
    if (!pendingApis.has(root)) {
      const state = () => {
        const required = Boolean(hasArtwork());
        return { required, canConfirm: !required && root.isConnected && !unloaded.has(root), accepted: false,
          status: required ? 'pending' : 'not-required', reason: 'preparation_loading', pendingZoneKeys: [] };
      };
      // This small, synchronous gate exists before the optional preparation
      // modules. An uploaded design is never accepted merely because they are
      // still loading or failed to load. Native price calculations stay eager.
      pendingApis.set(root, Object.freeze({ pending: true, readPadAcceptance: state,
        canConfirm: () => root.isConnected && !unloaded.has(root) && !hasArtwork(),
        focusPadPreparation() { report(false); requestPreparation(root, selector, true); return true; } }));
    }
    if (!preparationListeners.has(root)) {
      const lifetime = new AbortController();
      preparationListeners.set(root, lifetime);
      waitingRoots.add(root);
      const request = event => {
        const upload = event.type === 'drop' || event.target.closest?.('[data-role="logo-file-input"], [data-role="studio-upload-empty"], [data-role="studio-file-replace"], [data-role="recent-logo-upload"], [data-role="recent-logo"], [data-role="studio-add-design"]');
        requestPreparation(root, selector, Boolean(upload));
      };
      for (const event of [...events, 'change', 'click', 'focusin', 'drop']) root.addEventListener(event, request, { signal: lifetime.signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target === root || event.target.contains?.(root)) { unloaded.add(root); lifetime.abort(); waitingRoots.delete(root); }
      }, { signal: lifetime.signal });
    }
    if (!root.isConnected || unloaded.has(root)) return true;
    if (intent) preparationFailures.delete(root);
    else if (preparationFailures.has(root)) { if (!hasArtwork()) report(false); return true; }
    if (!intent && !preparationRequested.has(root) && !hasArtwork()) return true;
    preparationRequested.add(root);
    report(false);
    if (!preparationLoads.has(root)) {
      const task = modules.ensure(root, 'preparation');
      preparationLoads.set(root, task);
      task.then(() => {
        preparationReady.add(root);
        if (root.isConnected && !unloaded.has(root)) attach(root);
      }).catch(() => {
        // A later deliberate import can retry; no confirmation is inferred.
        preparationLoads.delete(root);
        preparationFailures.add(root);
        report(true);
      });
    }
    return true;
  }

  function attach(root) {
    if (!root?.isConnected || unloaded.has(root) || root.dataset.personalizationOrchestration !== 'v1') return null;
    if (instances.has(root)) return instances.get(root);
    const selector = root.querySelector('product-marking-selector'), gallery = root.querySelector('media-gallery');
    if (selector) ensureInformationCard(root, selector);
    if (requestPreparation(root, selector)) return null;
    const rules = window.TodayMerchPersonalizationTechniqueRules;
    const choice = root.querySelector('product-personalization-choice[data-personalization-v2]');
    const isXD = root.dataset.personalizationExperience === 'xd-v2';
    const isPFConcept = root.dataset.supplierPricing === 'pfconcept';
    const isLegacy = root.dataset.personalizationExperience === 'gobelet-v1';
    const legacy = isLegacy ? window.TodayMerchArtworkQuoteController?.get(root) : null;
    const preparationEngine = window.TodayMerchArtworkImagePreparation;
    const preparationUI = window.TodayMerchProductImagePreparation;
    const palette = window.TodayMerchArtworkPaletteAdapter;
    if (typeof palette?.canPrepare !== 'function' || typeof preparationEngine?.createSession !== 'function'
      || typeof preparationUI?.create !== 'function') return null;
    if (!selector || !gallery || !rules
      || (isXD ? !selector.supplierV2 : isLegacy ? !legacy?.context : !choice?.initialized || !['midocean', 'pfconcept'].includes(root.dataset.supplierPricing))) return null;
    preparationListeners.get(root)?.abort(); preparationListeners.delete(root); waitingRoots.delete(root);
    pendingApis.delete(root);
    const runtimeContexts = new Map();
    const lifetime = new AbortController(), fileIds = new WeakMap(), analyses = new WeakMap(), hashes = new WeakMap(), svgMeasurements = new WeakMap();
    const interacting = () => Boolean(gallery.designInteraction || gallery.mobileDesignEditor?.control || gallery.mobileDesignEditor?.gesture);
    let preparation = null;
    let sequence = 0, revision = 0, contextKey = '', generation = 0, timer, frame, fallback, gestureEndTimer, diagnosticExpiryTimer, pendingDiagnosticRequest = false, deferredForGesture = false,
      destroyed = false, diagnostic = null, api = null, dossier = null;
    const defaultPreviewMessage = 'Aperçu indicatif. Votre visuel sera vérifié avant encaissement. Si le rendu doit changer, nous vous demanderons votre accord.';
    let previewMessage = defaultPreviewMessage, previewBinding = null;
    const id = file => { if (!(file instanceof File)) return null; if (!fileIds.has(file)) fileIds.set(file, ++sequence); return fileIds.get(file); };
    const memoHash = file => { if (!hashes.has(file)) hashes.set(file, hash(file)); return hashes.get(file); };
    const memoAnalysis = file => {
      const analyzer = window.TodayMerchMidoceanArtworkFeasibility;
      if (!analyses.has(file)) analyses.set(file, analyzer.analyzeFile(file).catch(() => ({ status: 'review_required', verified: false })));
      return analyses.get(file);
    };
    const memoSvgMeasurements = file => {
      const measurementEngine = window.TodayMerchArtworkDesignPreflightMeasurements;
      if (typeof measurementEngine?.measureFile === 'function') {
        if (!svgMeasurements.has(file)) svgMeasurements.set(file, measurementEngine.measureFile(file, {
          decodePixels: palette.decodePixels,
          decodePdf: window.TodayMerchArtworkPdfAnalysis?.decode,
        }));
        return svgMeasurements.get(file);
      }
      if (file.type !== 'image/svg+xml' || typeof measurementEngine?.measureSvg !== 'function') return undefined;
      if (!svgMeasurements.has(file)) svgMeasurements.set(file, Promise.all([file.text(), memoHash(file)]).then(([text, sha256]) => ({ ...measurementEngine.measureSvg(text), sha256 })));
      return svgMeasurements.get(file);
    };
    const card = ensureInformationCard(root, selector);
    const { mount } = card;
    const placeNotes = card.place;
    placeNotes();
    // analyzeFile above extracts image facts only. Supplier requirements come
    // exclusively from this context; PF Concept has no inherited Midocean rules.
    const supplier = isXD ? 'xdconnects' : isPFConcept ? 'pfconcept' : 'midocean';
    const supplierId = isXD ? 'xd' : supplier;
    function nativeSnapshot(studio) {
      const owner = isXD ? selector.supplierV2 : isLegacy ? legacy : choice;
      if (isXD) {
        const snapshot = owner.snapshot({ purpose: 'dossier' });
        return { owner, snapshot: { ...snapshot, configurationRevision: snapshot.configurationRevision || owner.config.configurationRevision } };
      }
      if (isLegacy) {
        const native = owner.context(), source = native.source, variantId = String(native.entry?.variantId || studio?.variantId || '');
        const code = native.selection?.mode === 'personalized' ? native.selection.techniqueId : '';
        const supported = owner.config.techniqueCodes?.includes(code);
        const sku = owner.config.variantSkuMap?.[variantId]
          || (String(owner.config.variantId) === variantId ? owner.config.supplierSku : null);
        const measurement = window.TodayMerchProductDesignMeasurement?.attach(root);
        const placements = supported ? (native.entries || []).flatMap((entry, index) => {
          const zone = entry.zone || native.entry?.zone, artworkId = entry.item?.id || 'base';
          if (!zone) return [];
          const wrap = selector.getZoneWrap?.(zone.key) || selector.optionWraps?.find(value => value.dataset.zoneKey === zone.key);
          const files = wrap && selector.getLogoFiles?.(wrap) || [];
          const copy = gallery.getDesignArtworkCopy?.(zone.key, artworkId);
          const sourceArtworkId = copy?.sourceUploadId || artworkId;
          const uploaded = zone.artworks?.find(value => value.id === sourceArtworkId);
          const fileIndex = Number.isSafeInteger(uploaded?.index) ? uploaded.index : sourceArtworkId === 'base' ? 0 : files.findIndex(file => selector.getLogoFileKey?.(file) === entry.item?.fileKey);
          const original = files[fileIndex] || (index === 0 ? source?.original || source?.file : null);
          const production = original && selector.getArtworkProductionSource?.(original, zone.key, { artworkId }) || (index === 0 ? source : null);
          if (!(production?.file instanceof File) || !(original instanceof File)) return [];
          const measured = entry.item && measurement?.measureZone?.(zone, entry.placement, entry.variantId || variantId, artworkId) || (index === 0 ? native.dimensions : null);
          return [{ zoneKey: zone.key, artworkId, fileIndex, sourceArtworkId, techniqueId: code, original, file: production.file,
            artworkRevision: production.originalIdentity || String(id(original)), preparationRevision: String(production.revision),
            dimensions: measured?.available ? { widthMm: measured.preciseWidthMm ?? measured.widthMm, heightMm: measured.preciseHeightMm ?? measured.heightMm } : null,
            renderTransform: entry.placement || gallery.getDesignArtworkPlacement?.(zone, artworkId), placement: null,
            previewMatchesEffective: selector.getStudioPreviewSource?.(original, zone.key, { artworkId }) === production.file,
            ruleContext: { supplier, variantId, ...(owner.config.modelCode ? { productCode: owner.config.modelCode } : {}),
              ...(sku ? { sku } : {}), techniqueCode: code, positionCode: owner.config.zoneKey || zone.key, support: 'unknown' } }];
        }) : [];
        return { owner, snapshot: { placements, configurationRevision: owner.config.rulesVersion || owner.state?.artworkBinding?.publicProfileRevision || null, nativeContextKey: native.key,
          reason: placements.length !== (native.entries?.length || 0) ? 'artwork_source_unavailable' : null } };
      }

      const selected = owner.artworkConfiguration(studio);
      const measurement = window.TodayMerchProductDesignMeasurement?.attach(root);
      const entries = measurement?.entries?.() || [];
      const placements = [];
      for (const zone of studio?.zones || []) {
        const wrap = selector.optionWraps?.find(item => item.dataset.zoneKey === zone.key);
        const files = wrap ? selector.getLogoFiles?.(wrap) || [] : [];
        if (!files.length) continue;
        const configured = owner.zoneArtworkConfiguration?.(studio, zone.key);
        const technique = configured?.status === 'selected' ? configured.technique : selected.zoneKeys?.length === 1 && selected.zoneKeys[0] === zone.key ? selected.technique : null;
        const artworks = gallery.getDesignArtworkItems?.(zone) || files.map((file, index) => ({ id: zone.artworks?.[index]?.id || (index ? `uploaded-${index}` : 'base'), fileKey: selector.getLogoFileKey?.(file) }));
        for (const artwork of artworks) {
          const copy = gallery.getDesignArtworkCopy?.(zone.key, artwork.id), uploadId = copy?.sourceUploadId || (artwork.id === 'base' ? 'base' : '');
          const sources = (zone.artworks || []).filter(entry => uploadId ? entry.id === uploadId : entry.fileKey === artwork.fileKey);
          const candidates = files.map((file, index) => selector.getLogoFileKey?.(file) === artwork.fileKey ? index : -1).filter(index => index >= 0);
          const fileIndex = sources.length === 1 && Number.isInteger(sources[0].index) ? sources[0].index : artwork.id === 'base' ? 0 : candidates.length === 1 ? candidates[0] : -1;
          const original = files[fileIndex], source = original && selector.getArtworkProductionSource?.(original, zone.key, { artworkId: artwork.id });
          if (!(original instanceof File) || !(source?.file instanceof File) || artwork.fileKey && selector.getLogoFileKey?.(original) !== artwork.fileKey) continue;
          const entry = entries.find(item => item.zone.key === zone.key && (item.item?.id || 'base') === artwork.id);
          const transform = gallery.getDesignArtworkPlacement?.(zone, artwork.id);
          const measured = entry ? measurement.measureZone(entry.zone, entry.placement, entry.variantId, entry.item.id) : null;
          const canonical = measured?.canonicalPlacementMm;
          const position = canonical && [canonical.preciseXMm ?? canonical.xMm, canonical.preciseYMm ?? canonical.yMm, canonical.rotationDeg].every(Number.isFinite)
            ? { xMm: canonical.preciseXMm ?? canonical.xMm, yMm: canonical.preciseYMm ?? canonical.yMm, rotationDeg: canonical.rotationDeg, crop: null } : null;
          const renderTransform = Object.fromEntries(['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop']
            .filter(key => Number.isFinite(transform?.[key])).map(key => [key, transform[key]]));
          placements.push({ zoneKey: zone.key, artworkId: artwork.id, fileIndex, sourceArtworkId: sources[0]?.id || (fileIndex === 0 ? 'base' : ''),
          techniqueId: technique?.id || '', techniqueLabel: technique?.label || '', original, file: source.file,
          artworkRevision: String(source.originalIdentity || id(original)) + (artworks.length > 1 ? `:artwork:${artwork.id}` : ''),
          preparationRevision: String(source.revision), renderTransform,
          dimensions: measured?.available ? { widthMm: measured.preciseWidthMm ?? measured.widthMm, heightMm: measured.preciseHeightMm ?? measured.heightMm } : null,
          placement: position, previewMatchesEffective: selector.getStudioPreviewSource?.(original, zone.key, { artworkId: artwork.id }) === source.file,
          ruleContext: { supplier, variantId: String(studio.variantId || ''), techniqueCode: technique?.id || '',
            positionCode: technique?.supplierPositionId || selected.current?.zone?.supplierPositionId || '', support: 'unknown' } });
        }
      }
      return { owner, snapshot: { placements, configurationRevision: owner.config.revision,
        reason: selected.status === 'selected' ? null : selected.status } };
    }

    function readOwner() {
      const owner = isXD && selector.supplierV2;
      return typeof owner?.withOfferResolution === 'function' ? owner.withOfferResolution(readOwnerSnapshot) : readOwnerSnapshot();
    }
    function readOwnerSnapshot() {
      const studio = selector.getStudioState?.();
      const { owner, snapshot } = nativeSnapshot(studio);
      const zoneKey = selector.activeZoneKey || studio?.activeZoneKey || gallery.getActiveDesignZone?.()?.key || '';
      // Native dossier readiness can reject a placement before returning any
      // files. The colour editor still needs the real local design and exact
      // selected offer; this draft projection never changes native readiness.
      const uiPlacements = (snapshot.placements || []).map(item => ({ ...item }));
      if (isXD) for (const wrap of selector.optionWraps || []) {
        const key = wrap.dataset.zoneKey, files = selector.getLogoFiles?.(wrap) || [];
        if (uiPlacements.some(item => item.zoneKey === key) || files.length !== 1 || !(files[0] instanceof File)) continue;
        const offer = owner.offer(key), original = files[0], source = selector.getArtworkProductionSource?.(original, key);
        if (!offer || !(source?.file instanceof File)) continue;
        const nativeZone = studio?.zones?.find(item => item.key === key);
        const placement = nativeZone && gallery.getDesignArtworkPlacement?.(nativeZone, 'base');
        let physical = null;
        try { physical = owner.readPersonalizationResumeGeometry?.(key, placement); } catch { /* Geometry stays unresolved. */ }
        uiPlacements.push({ zoneKey: key, techniqueId: offer.techniqueId, kind: offer.kind, original, file: source.file,
          artworkRevision: String(source.originalIdentity || id(original)), preparationRevision: `preparation:${source.revision || id(source.file)}`,
          dimensions: physical?.dimensions || null, placement: physical?.placement || null,
          ruleContext: { supplier, variantId: String(studio?.variantId || ''),
            techniqueCode: offer.techniqueCode || offer.printCode || offer.techniqueId }, placementReviewRequired: true });
      }
      for (const item of uiPlacements) {
        const sourceZone = studio?.zones?.find(zone => zone.key === item.zoneKey);
        item.contentFrame = gallery.getDesignArtworkContentFrame?.(sourceZone, item.artworkId || 'base') || item.renderTransform?.contentFrame || null;
      }
      const artworkId = gallery.getActiveDesignArtworkId?.(zoneKey) || 'base';
      const selected = uiPlacements.find(item => item.zoneKey === zoneKey && (item.artworkId || 'base') === artworkId) || null;
      const zone = studio?.zones?.find(item => item.key === zoneKey);
      const transform = zone ? gallery.getDesignArtworkPlacement?.(zone, artworkId) : null;
      const quantity = root.querySelector('input[name="quantity"]')?.value || '';
      const productionPriority = window.TodayMerchProductOrderSummary?.get(root)?.priority
        || root.querySelector('[data-production-priority]:checked')?.value || null;
      const variantId = String(gallery.activeVariantId || studio?.variantId || '');
      const nativeCurrent = !isXD && !isLegacy ? owner.current(studio) : null;
      const nativeConfigured = !isXD && !isLegacy ? owner.zoneArtworkConfiguration?.(studio, zoneKey) : null;
      const offer = isXD ? owner.offer(zoneKey) : isLegacy ? (selected ? { id: selected.techniqueId, maxColors: owner.config.maxColors } : null)
        : nativeConfigured?.status === 'selected' ? nativeConfigured.technique
          : nativeCurrent?.techniques.find(item => item.id === owner.getSelection(studio).techniqueId);
      const offers = isXD ? owner.offers(zoneKey, { readOnly: true }) : isLegacy
        ? (owner.config.techniqueCodes || []).map(id => ({ id })) : nativeCurrent?.techniques || [];
      const identity = { productId: root.dataset.productId, variantId, configurationRevision: snapshot.configurationRevision || owner.config?.configurationRevision,
        rulesRevision: rules.VERSION, nativeContextKey: snapshot.nativeContextKey, zoneKey, quantity, productionPriority, reason: snapshot.reason || null,
        artworkId, placements: uiPlacements.map(item => ({ zoneKey: item.zoneKey, artworkId: item.artworkId || 'base', techniqueId: item.techniqueId,
          original: id(item.original), effective: id(item.file), artworkRevision: item.artworkRevision,
          preparationRevision: item.preparationRevision, dimensions: item.dimensions, placement: item.placement, contentFrame: item.contentFrame,
          renderTransform: item.renderTransform || gallery.getDesignArtworkPlacement?.(studio?.zones?.find(zone => zone.key === item.zoneKey), item.artworkId || 'base') })), transform };
      const key = JSON.stringify(identity);
      if (key !== contextKey) { contextKey = key; revision++; generation++; diagnostic = null; previewMessage = defaultPreviewMessage; if (api) api.lastDiagnostic = null; }
      return { owner, studio, snapshot, uiPlacements, zoneKey, artworkId, zone, selected, transform, quantity, productionPriority, variantId, offer, offers, supplier, supplierId, key, contextKey: key, revision: String(revision) };
    }
    function readDossierOwner(current = readOwner()) {
      const { uiPlacements, ...native } = current;
      return { ...native, selected: current.snapshot.reason ? null
        : current.snapshot.placements?.find(item => item.zoneKey === current.zoneKey && (item.artworkId || 'base') === current.artworkId) || null };
    }
    function readApplicationOwner(zoneKey, current = readOwner()) {
      if (!zoneKey || zoneKey === current.zoneKey) return current;
      const selected = (current.uiPlacements || current.snapshot.placements || []).find(item => item.zoneKey === zoneKey);
      const zone = current.studio?.zones?.find(item => item.key === zoneKey);
      if (!selected || !zone) return null;
      const configured = !isXD && !isLegacy ? current.owner.zoneArtworkConfiguration?.(current.studio, zoneKey) : null;
      const projected = !isXD && !isLegacy ? current.owner.current?.({ ...current.studio, activeZoneKey: zoneKey }) : null;
      const offers = isXD ? current.owner.offers?.(zoneKey) || current.offers : projected?.techniques || current.offers;
      const offer = isXD ? current.owner.offer(zoneKey) : isLegacy ? { id: selected.techniqueId, maxColors: current.owner.config.maxColors }
        : configured?.status === 'selected' ? configured.technique : offers.find(item => techniqueId(item) === selected.techniqueId);
      return { ...current, selected, zone, zoneKey, offer, offers, transform: gallery.getDesignArtworkPlacement?.(zone, 'base') || null };
    }
    // A displayed proposal is a draft. Confirmation requires an exact engine
    // receipt: explicit consent, or a separately verified technical application.
    const padAccepted = new Map(), padHistory = new Map(), padEditors = new Map(), padObserved = new Map();
    const automaticTickets = new Map();
    const technicalApplications = new Map();
    let padSerial = 0, padSignature = '', padVariant = '', padRollback = false;
    const padKey = (current, zoneKey = current.zoneKey, artworkId = zoneKey === current.zoneKey ? current.artworkId : 'base') => `${current.variantId}|${zoneKey}|${artworkId || 'base'}`;
    const techniqueId = offer => offer?.techniqueId || offer?.id || '';
    function padOffer(current, item) {
      if (item.zoneKey === current.zoneKey) return current.offer;
      if (isXD) return current.owner.offer(item.zoneKey);
      return current.offers.find(offer => techniqueId(offer) === item.techniqueId);
    }
    function isPad(current, item, offer = padOffer(current, item)) {
      const nativeCode = item?.ruleContext?.techniqueCode || offer?.techniqueCode || offer?.printCode || item?.techniqueId;
      const code = supplier === 'xdconnects'
        ? window.TodayMerchProductImagePreparation?.resolveXdTechniqueCode?.(nativeCode) ?? nativeCode : nativeCode;
      return rules.resolveTechniqueRules({ supplier, techniqueCode: code })?.technique?.family === 'pad_print'
        || (supplier === 'midocean' && /^P[1-7]$/.test(code || ''))
        || (supplier === 'xdconnects' && ['pad-print', 'pad_print', 'pad_print_drinkware', 'Pad Print Low', 'Pad Print Drinkware'].includes(code));
    }
    function supplierColorLimit(current, item, offer = padOffer(current, item)) {
      let cap = offer?.maxColors;
      if (cap == null && isXD) cap = window.TodayMerchSupplierPricing?.getArtworkColorLimit?.(root, {
        productId: root.dataset.productId, configurationRevision: current.snapshot.configurationRevision,
        variantId: current.variantId, zoneKey: item.zoneKey, techniqueId: item.techniqueId,
        kind: item.kind || offer?.kind, shape: offer?.shape, maxWidthMm: offer?.maxWidthMm,
        maxHeightMm: offer?.maxHeightMm, dimensions: item.dimensions })?.maxColors;
      return Number.isInteger(cap) && cap > 0 ? cap : null;
    }
    function padLimit(current, item, offer = padOffer(current, item)) {
      const cap = supplierColorLimit(current, item, offer);
      return cap && cap <= 5 ? cap : null;
    }
    function padRevision(current) {
      // The legacy quote binding is transient while the price is recalculated.
      // Colour acceptance follows print capabilities, never a price response.
      return isLegacy ? JSON.stringify([current.owner.config.rulesVersion || rules.VERSION,
        current.owner.config.maxColors, current.owner.config.techniqueCodes, current.owner.config.zoneKey,
        current.owner.config.modelCode, current.owner.config.variantSkuMap]) : current.snapshot.configurationRevision;
    }
    function matching(record, current, item) {
      return record && record.variantId === current.variantId && record.zoneKey === item.zoneKey
        && (record.artworkId || 'base') === (item.artworkId || 'base')
        && record.techniqueId === item.techniqueId && record.original === item.original && record.file === item.file
        && record.configurationRevision === padRevision(current)
        && (!record.accepted?.automatic || automaticBinding(record.accepted.receipt, current, item));
    }
    function automaticBinding(receipt, current, item, requirePadLimit = true) {
      const applied = preparationEngine.getAppliedPreparation?.(item.file, receipt);
      const supplierCap = requirePadLimit ? padLimit(current, item) : supplierColorLimit(current, item);
      if (!applied || applied.receipt !== receipt || applied.originalFile !== item.original || applied.effectiveFile !== item.file
        || receipt.application?.mode !== 'automatic_compatible' || receipt.consent !== null
        || !supplierCap || receipt.application.maxColors !== Math.min(supplierCap, 5)) return false;
      // The caller already read the complete owner in this synchronous check.
      // Project its requested zone without reconstructing every placement again.
      const itemContext = current.selected === item ? current : { ...current, zoneKey: item.zoneKey, artworkId: item.artworkId || 'base', selected: item,
        zone: current.studio?.zones?.find(zone => zone.key === item.zoneKey), transform: item.renderTransform || current.transform };
      const live = preparation?.readApplicationContext?.(item.zoneKey, itemContext);
      const context = applied.appliedContext;
      if (!live || !context || String(live.productId) !== String(root.dataset.productId)
        || String(live.variantId) !== current.variantId || live.positionId !== item.zoneKey
        || live.techniqueId !== item.techniqueId || live.originalFile !== item.original || live.effectiveFile !== item.file
        || String(live.preparationRevision) !== String(item.preparationRevision)) return false;
      const serialize = value => JSON.stringify(value == null ? null : typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(Object.keys(value).sort().map(key => [key, JSON.parse(serialize(value[key]))])) : value);
      const keys = ['productId', 'variantId', 'supplier', 'techniqueId', 'positionId', 'zoneKey', 'artworkRevision',
        'preparationRevision', 'rulesRevision', 'configurationRevision', 'capabilityRevision', 'priceRevision',
        'capabilitiesValidUntil', 'dimensionsMm', 'placement'];
      return keys.every(key => serialize(live[key]) === serialize(context[key]))
        && (context.capabilitiesValidUntil == null || Date.parse(context.capabilitiesValidUntil) > Date.now());
    }
    function capture(current, item, accepted = null) {
      return { variantId: current.variantId, zoneKey: item.zoneKey, artworkId: item.artworkId || 'base', fileIndex: item.fileIndex || 0, techniqueId: item.techniqueId,
        original: item.original, file: item.file, kind: selector.getArtworkProductionSource?.(item.original, item.zoneKey, { artworkId: item.artworkId || 'base' })?.kind,
        configurationRevision: padRevision(current),
        colors: accepted?.colors || current.owner.colors || current.owner.context?.().selection?.colors || 1,
        transform: item.renderTransform ? { ...item.renderTransform } : current.zoneKey === item.zoneKey && current.transform ? { ...current.transform } : null, accepted };
    }
    function unresolvedPad(current) {
      if (isXD) return null;
      const code = isLegacy ? current.owner.context().selection?.techniqueId : current.owner.techniqueId;
      if (!code || !isPad(current, { techniqueId: code }, { id: code })) return null;
      const resolved = current.uiPlacements || current.snapshot.placements || [];
      const zones = (selector.optionWraps || []).map(wrap => ({ zoneKey: wrap.dataset.zoneKey,
        files: selector.getLogoFiles?.(wrap) || [] })).filter(item => item.files.length);
      const unresolved = zones.some(item => !resolved.some(placement => placement.zoneKey === item.zoneKey && placement.techniqueId
        && item.files.includes(placement.original)));
      if (!unresolved || zones.reduce((count, zone) => count + zone.files.length, 0) < 2) return null;
      return { techniqueId: code, zones };
    }
    function reconcilePad(current) {
      if (padRollback || destroyed) return;
      if (padVariant && padVariant !== current.variantId) { padAccepted.clear(); padEditors.clear(); technicalApplications.clear(); padSerial++; }
      padVariant = current.variantId;
      const placements = current.uiPlacements || current.snapshot.placements || [];
      const keys = new Set(placements.map(item => padKey(current, item.zoneKey, item.artworkId)));
      for (const key of padHistory.keys()) if (!unresolvedPad(current) && key.startsWith(`${current.variantId}|`) && !keys.has(key)) padHistory.delete(key);
      for (const item of placements) {
        const key = padKey(current, item.zoneKey, item.artworkId);
        const signature = JSON.stringify([item.techniqueId, id(item.original), id(item.file),
          padRevision(current), padLimit(current, item)]);
        if (padObserved.has(key) && padObserved.get(key) !== signature) padSerial++;
        padObserved.set(key, signature);
        if (!isPad(current, item)) {
          padAccepted.delete(key); padEditors.delete(key);
          if (item.techniqueId && item.original instanceof File && item.file instanceof File) padHistory.set(key, capture(current, item));
          continue;
        }
        const accepted = padAccepted.get(key);
        if (accepted && (!matching(accepted, current, item) || !padLimit(current, item) || accepted.colors > padLimit(current, item))) {
          padAccepted.delete(key); padSerial++;
        }
      }
    }
    function padState(current = readOwner()) {
      reconcilePad(current);
      const unresolved = unresolvedPad(current);
      if (unresolved) {
        const zone = unresolved.zones.find(item => item.zoneKey === current.zoneKey) || unresolved.zones[0];
        return Object.freeze({ required: true, accepted: false, canConfirm: false, status: 'pending',
          reason: 'choose_one_placement', zoneKey: zone.zoneKey, techniqueId: unresolved.techniqueId,
          originalFile: zone.files[0], effectiveFile: selector.getArtworkProductionSource?.(zone.files[0], zone.zoneKey)?.file || zone.files[0],
          pendingZoneKeys: unresolved.zones.map(item => item.zoneKey) });
      }
      const placements = (current.uiPlacements || current.snapshot.placements || []).filter(item => isPad(current, item));
      const pending = placements.filter(item => !matching(padAccepted.get(padKey(current, item.zoneKey, item.artworkId)), current, item)
        || padEditors.get(padKey(current, item.zoneKey, item.artworkId))?.dirty);
      const item = pending.find(item => item.zoneKey === current.zoneKey && (item.artworkId || 'base') === current.artworkId) || pending[0] || current.selected;
      const automatic = placements.some(placement => padAccepted.get(padKey(current, placement.zoneKey, placement.artworkId))?.accepted?.automatic);
      return Object.freeze({ required: placements.length > 0, accepted: placements.length > 0 && pending.length === 0 && !automatic,
        automatic: pending.length === 0 && automatic, applicationMode: pending.length === 0 && automatic ? 'automatic_compatible' : null,
        productionReady: false, canConfirm: pending.length === 0,
        status: pending.length ? 'pending' : placements.length ? automatic ? 'applied' : 'accepted' : 'not-required',
        zoneKey: item?.zoneKey || current.zoneKey, artworkId: item?.artworkId || 'base', techniqueId: item?.techniqueId || '',
        originalFile: item?.original || null, effectiveFile: item?.file || null, pendingZoneKeys: pending.map(item => item.zoneKey) });
    }
    function preparationState(current = readOwner()) {
      const pad = padState(current), treatment = preparation?.readAcceptance?.(current);
      return !pad.canConfirm ? pad : treatment?.required ? treatment : pad;
    }
    function publishPad(current = readOwner()) {
      const state = preparationState(current);
      const signature = JSON.stringify([state.status, state.reason, current.variantId, state.pendingZoneKeys,
        state.techniqueId, id(state.originalFile), id(state.effectiveFile)]);
      root.dataset.padAcceptance = state.status;
      let guidance = mount.querySelector('[data-pad-placement-guidance]');
      if (state.reason === 'choose_one_placement' && !root.querySelector('[data-artwork-color-inline]')) {
        if (!guidance) {
          guidance = document.createElement('div'); guidance.dataset.padPlacementGuidance = '';
          guidance.className = 'artwork-image-preparation';
          const text = document.createElement('p'); text.textContent = 'Choisissez un seul emplacement pour ce marquage.';
          const cancel = document.createElement('button'); cancel.type = 'button'; cancel.textContent = 'Annuler';
          cancel.className = 'artwork-color-dialog__button'; cancel.addEventListener('click', () => cancelPadDraft());
          guidance.append(text, cancel); mount.append(guidance);
        }
        guidance.hidden = false;
      } else if (guidance) guidance.hidden = true;
      if (signature !== padSignature) {
        padSignature = signature;
        root.dispatchEvent(new CustomEvent('todaymerch:pad-acceptance-change', { bubbles: true, detail: state }));
      }
      return state;
    }
    function boundDraft(detail, current = readOwner()) {
      const item = current.selected;
      return !destroyed && root.isConnected && item && String(detail?.productId) === String(root.dataset.productId)
        && String(detail.variantId) === current.variantId && detail.zoneKey === current.zoneKey
        && detail.techniqueId === item.techniqueId && detail.originalFile === item.original
        && detail.effectiveFile === item.file && String(detail.sourceRevision) === String(item.preparationRevision);
    }
    function onPadDraft(detail) {
      const current = readOwner(); if (!boundDraft(detail, current) || !isPad(current, current.selected)) return;
      const key = padKey(current), previous = padEditors.get(key);
      // A fresh editor merely displays the accepted file. Only a deliberate
      // modification suspends its acceptance; cancellation can restore it.
      padEditors.set(key, { editorId: detail.editorId, dirty: detail.dirty === true,
        ready: detail.ready === true, original: detail.originalFile, file: detail.effectiveFile, sourceRevision: detail.sourceRevision });
      if (detail.dirty) {
        automaticTickets.delete(key);
        if (!previous?.dirty || previous.editorId !== detail.editorId) padSerial++;
      }
      publishPad(current);
    }
    async function acceptPad(detail) {
      const current = readOwner(), item = current.selected;
      if (!item || !isPad(current, item) || String(detail?.productId) !== String(root.dataset.productId)
        || String(detail.variantId) !== current.variantId || detail.zoneKey !== current.zoneKey
        || detail.techniqueId !== item.techniqueId || detail.originalFile !== item.original || detail.effectiveFile !== item.file) return;
      // The source-change event may now reconcile after paint. Observe its
      // actual file transition before issuing the asynchronous acceptance ticket.
      reconcilePad(current);
      const key = padKey(current), editor = padEditors.get(key);
      const automatic = detail.automatic === true;
      if (automatic && editor?.dirty) return;
      if (!automatic && (!editor || editor.editorId !== detail.editorId || !editor.ready || editor.original !== detail.originalFile
        || editor.file !== (detail.previousEffectiveFile || detail.baseFile) || String(editor.sourceRevision) !== String(detail.sourceRevision))) return;
      const receipt = detail.receipt, ticket = ++padSerial;
      if (automatic) automaticTickets.set(key, ticket);
      try {
        if (automatic) {
          if (!automaticBinding(receipt, current, item)
            || String(detail.appliedSourceRevision) !== String(item.preparationRevision)) return;
        } else preparationEngine.toDossierAdaptation(receipt);
        const binding = receipt.context, cap = padLimit(current, item);
        const colors = [...receipt.transformations].reverse().find(operation => Array.isArray(operation.palette))?.palette;
        if ((!automatic && receipt.consent?.action !== 'use_this_version') || receipt.plan?.restoreOriginal || !cap
          || !Array.isArray(colors) || colors.length < 1 || colors.length > cap
          || new Set(colors).size !== colors.length || colors.some(color => !/^#[a-f\d]{6}$/i.test(color))
          || String(binding.productId) !== String(root.dataset.productId) || String(binding.variantId) !== current.variantId
          || binding.positionId !== item.zoneKey || binding.techniqueId !== item.techniqueId
          || String(binding.preparationRevision) !== String(detail.sourceRevision)) return;
        const [originalHash, effectiveHash] = await Promise.all([memoHash(item.original), memoHash(item.file)]);
        const latest = automatic ? readApplicationOwner(item.zoneKey) : readOwner(), latestItem = latest?.selected;
        if (destroyed || (automatic ? automaticTickets.get(key) !== ticket : ticket !== padSerial) || !latestItem || latest.variantId !== current.variantId
          || latest.zoneKey !== current.zoneKey || latestItem.techniqueId !== item.techniqueId
          || latestItem.original !== item.original || latestItem.file !== item.file
          || padRevision(latest) !== padRevision(current)
          || originalHash !== receipt.original.sha256 || effectiveHash !== receipt.effective.sha256
          || automatic && (padEditors.get(key)?.dirty || !automaticBinding(receipt, latest, latestItem))
          || !padLimit(latest, latestItem) || colors.length > padLimit(latest, latestItem)) return;
        const accepted = capture(latest, latestItem, { colors: colors.length, receipt, automatic }); accepted.colors = colors.length;
        padAccepted.set(key, accepted); padHistory.set(key, accepted); padEditors.delete(key); publishPad(automatic ? readOwner() : latest);
      } catch { /* Missing or stale receipt keeps the actual design unconfirmed. */ }
    }
    function focusPadPreparation() {
      const state = preparationState();
      if (state.zoneKey && selector.activeZoneKey !== state.zoneKey) selector.setActiveStudioZone?.(state.zoneKey);
      if (state.zoneKey && state.artworkId && gallery.getActiveDesignArtworkId?.(state.zoneKey) !== state.artworkId) {
        gallery.setActiveDesignArtworkId?.(state.zoneKey, state.artworkId); gallery.renderDesignStudio?.();
      }
      if (state.reason === 'choose_one_placement') {
        const guidance = mount.querySelector('[data-pad-placement-guidance]') || mount;
        guidance.setAttribute('tabindex', '-1'); guidance.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        guidance.focus({ preventScroll: true }); return true;
      }
      if (preparation?.open?.()) return !state.canConfirm;
      const editor = mount;
      editor.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      const control = editor.querySelector('[data-artwork-color-count]:not([disabled]),[data-artwork-color-hex]:not([disabled]),[data-artwork-color-apply]:not([disabled])');
      if (!control) editor.setAttribute('tabindex', '-1');
      (control || editor).focus({ preventScroll: true });
      return !state.canConfirm;
    }
    function cancelPadDraft(detail) {
      const current = readOwner();
      const unresolved = unresolvedPad(current);
      if (destroyed || padRollback || !unresolved && (!current.selected || !isPad(current, current.selected))) return false;
      if (detail && (!boundDraft(detail, current) || padEditors.get(padKey(current))?.editorId !== detail.editorId)) return false;
      const affected = unresolved?.zones.map(item => item.zoneKey) || [current.zoneKey];
      const saved = padHistory.get(padKey(current)) || (unresolved && affected.map(zoneKey => padHistory.get(padKey(current, zoneKey))).find(Boolean));
      const zoneKey = saved?.zoneKey || current.zoneKey, key = padKey(current, zoneKey);
      const wrap = selector.getZoneWrap?.(zoneKey) || selector.optionWraps?.find(item => item.dataset.zoneKey === zoneKey);
      padRollback = true; padSerial++;
      for (const zone of affected) { const key = padKey(current, zone); padEditors.delete(key); padAccepted.delete(key); automaticTickets.delete(key); }
      try {
        for (const zone of affected) if (zone !== zoneKey) selector.clearLogoForZone?.(zone);
        if (selector.activeZoneKey !== zoneKey) selector.setActiveStudioZone?.(zoneKey, { selectInput: false, broadcast: false });
        const offers = !isXD && !isLegacy ? current.owner.current({ ...current.studio, activeZoneKey: zoneKey }).techniques : current.offers;
        const savedOffer = saved && offers.find(offer => techniqueId(offer) === saved.techniqueId && offer.available !== false);
        const savedCap = savedOffer && (isLegacy ? current.owner.config.maxColors
          : padLimit(current, { zoneKey, techniqueId: saved.techniqueId, dimensions: current.selected?.dimensions }, savedOffer));
        const available = savedOffer && (!saved.accepted || saved.configurationRevision === padRevision(current)
          && savedCap && saved.colors <= savedCap);
        const currentFiles = wrap && selector.getLogoFiles?.(wrap) || [];
        const multipleArtworks = currentFiles.length > 1 || (gallery.getDesignArtworkCopies?.(zoneKey) || []).length > 0;
        const restoredFiles = multipleArtworks ? currentFiles.map((file, index) => index === saved?.fileIndex ? saved.original : file) : saved ? [saved.original] : [];
        if (!available || !wrap || selector.setLogoFilesForWrap?.(wrap, restoredFiles) === false) {
          if (multipleArtworks && current.selected?.original) selector.selectArtworkProductionSource?.(current.selected.original, null, { kind: 'original', zoneKey, artworkId: current.artworkId || 'base' });
          else selector.clearLogoForZone?.(zoneKey);
        } else {
          const input = wrap.querySelector('[data-role="marking-input"]'); if (input) input.checked = true;
          if (isXD) current.owner.select(zoneKey, saved.techniqueId);
          else if (isLegacy) {
            const owner = current.owner.context().choice;
            owner.mode = 'personalized'; owner.techniqueId = saved.techniqueId; owner.colors = saved.colors; owner.sync(true);
          } else current.owner.selectTechnique(saved.techniqueId);
          selector.selectArtworkProductionSource(saved.original, saved.file === saved.original ? null : saved.file,
            { kind: saved.file === saved.original ? 'original' : saved.kind || 'adapted', zoneKey, artworkId: saved.artworkId || 'base' });
          selector.storeCurrentVariantLogoState?.(); selector.syncUI?.();
          if (saved.transform && gallery.designArtworkPlacements && gallery.getScopedDesignZoneKey) {
            if (saved.artworkId && saved.artworkId !== 'base') gallery.updateDesignArtworkCopy?.(zoneKey, saved.artworkId, { placement: { ...saved.transform } });
            else gallery.designArtworkPlacements.set(gallery.getScopedDesignZoneKey(zoneKey), { ...saved.transform });
            gallery.renderDesignStudio?.();
          }
          if (saved.accepted) padAccepted.set(key, saved);
        }
      } finally { padRollback = false; schedule(); }
      return true;
    }
    function read() {
      const current = readOwner(), zone = current.zone;
      const history = zone ? gallery.getDesignArtworkHistory?.(zone.key, current.artworkId || 'base') : null;
      return { revision: current.revision, contextKey: current.key,
        history: { canUndo: !!history?.past?.length, canRedo: !!history?.future?.length, label: 'Historique du placement sur cet emplacement. Original conservé.' },
        ...(dossier?.read(readDossierOwner(current)) || { draft: { available: false }, persistence: { state: 'unavailable', available: false, message: 'Enregistrement du dossier indisponible sur cette fiche. Votre original reste dans cette session.' } }),
        preview: { message: previewBinding === current.key ? previewMessage : defaultPreviewMessage }, diagnostic: diagnostic || { revision: current.revision, contextKey: current.key,
          state: current.selected ? 'review' : 'unknown', message: reviewReminder },
        review: { message: 'Aperçu et diagnostic partiel : validation de production non effectuée.' } };
    }
    async function execute(command, { signal } = {}) {
      const current = readOwner();
      if (signal?.aborted || command.expectedRevision !== current.revision || command.contextKey !== current.key) throw fail('stale');
      if (['save', 'resume', 'dismiss-draft'].includes(command.type)) {
        if (!dossier) throw fail('unsupported');
        return dossier.execute(command, { signal });
      }
      if (command.type === 'undo' || command.type === 'redo') {
        if (!current.zone) throw fail('unsupported');
        gallery.restoreDesignArtworkHistory(current.zoneKey, command.type, current.artworkId || 'base'); gallery.renderDesignStudio?.(); schedule(); return;
      }
      if (command.type === 'retry-diagnostic') { retryDiagnostic(current); return; }
      throw fail('unsupported');
    }
    function replayAppliedPreparation(candidate = dossier, ownerSnapshot) {
      if (!candidate || candidate !== dossier || destroyed || !root.isConnected) return;
      const current = readDossierOwner(ownerSnapshot), item = current.selected;
      if (!item) return;
      const known = padAccepted.get(padKey(current, item.zoneKey, item.artworkId))?.accepted;
      const receipt = known?.automatic ? known.receipt : technicalApplications.get(padKey(current, item.zoneKey, item.artworkId));
      const applied = preparationEngine.getAppliedPreparation?.(item.file, receipt || null);
      if (!applied || !(applied.baseFile instanceof File) || !automaticBinding(applied.receipt, current, item, isPad(current, item))) return;
      candidate.applied?.({ automatic: true, productId: root.dataset.productId, variantId: current.variantId,
        zoneKey: item.zoneKey, techniqueId: item.techniqueId, originalFile: item.original, effectiveFile: item.file,
        baseFile: applied.baseFile, receipt: applied.receipt, sourceRevision: applied.context.preparationRevision,
        appliedSourceRevision: applied.appliedContext.preparationRevision });
    }
    function connectDossier() {
      if (dossier || !dossierFactory || destroyed) return;
      try {
        const candidate = dossierFactory.create({ root, selector, gallery, readOwner: readDossierOwner,
          readApplicationContext: zoneKey => preparation?.readApplicationContext?.(zoneKey),
          recoverApplication: () => replayAppliedPreparation(), notify: () => refreshView() });
        if (candidate && typeof candidate.read === 'function' && typeof candidate.execute === 'function') {
          dossier = candidate;
          replayAppliedPreparation(candidate);
          Promise.resolve(candidate.ready).then(() => replayAppliedPreparation(candidate)).catch(() => {});
        }
        else candidate?.destroy?.();
      } catch { /* Native editing remains available if the authenticated service is absent. */ }
      if (api) api.dossier = dossier;
      refreshView();
    }
    function retryDiagnostic(current) {
      const selected = current?.selected;
      if (selected?.file) { analyses.delete(selected.file); svgMeasurements.delete(selected.file); }
      runtimeContexts.delete(diagnosticContextRequest(current).key);
      pendingDiagnosticRequest = true;
      schedule();
    }
    function refreshView() {
      if (!destroyed) placeNotes();
    }

    function diagnosticContextRequest(current) {
      const selection = { productId: String(root.dataset.productId || ''), variantId: String(current.variantId || ''), techniqueId: String(current.selected?.techniqueId || ''), positionId: String(current.zoneKey || '') };
      return { selection, key: JSON.stringify([selection, current.snapshot.configurationRevision, rules.VERSION]) };
    }
    async function runtimeDiagnosticContext(current) {
      const { selection, key } = diagnosticContextRequest(current);
      if (Object.values(selection).some(value => !value)) return null;
      const cached = runtimeContexts.get(key);
      if (cached && cached.expiresAt > Date.now()) return cached.promise;
      // The server allows 15 seconds; reserve another 5 for transport and keep
      // the same in-flight request reusable throughout that client budget.
      const timeoutMs = 20000;
      const entry = { expiresAt: Date.now() + timeoutMs, promise: null };
      entry.promise = (async () => {
        const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), timeoutMs);
        const abort = () => controller.abort(); lifetime.signal.addEventListener('abort', abort, { once: true });
        try {
          const url = new URL(root.dataset.personalizationDiagnosticEndpoint || '/apps/todays-studio/personalization-diagnostics/context', location.href);
          if (url.origin !== location.origin) return null;
          for (const [name, value] of Object.entries(selection)) url.searchParams.set(name, value);
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) return null;
          const value = await response.json(), documentary = value.validUntil === null, expires = Date.parse(value.validUntil);
          if (!value.ruleContext || !value.rules || !value.selection || !documentary && (!Number.isFinite(expires) || expires <= Date.now())
            || !Object.entries(selection).every(([name, selected]) => String(value.selection[name]) === selected)
            || value.productionReady !== false) return null;
          if (documentary) {
            // Missing plane freshness does not erase independently sourced
            // file rules. Their evidence keeps its own freshness checks.
            entry.expiresAt = Date.now() + 5 * 60 * 1000;
            return { ...value, diagnosticContextStatus: 'documentary', diagnosticExpiresAt: entry.expiresAt,
              physicalBinding: { ...value.physicalBinding, status: 'unknown', reason: 'documentary_context_without_expiry' },
              rules: { ...value.rules, combination: { ...value.rules.combination,
                status: value.rules.combination?.status === 'documented' ? 'historically_documented' : value.rules.combination?.status } } };
          }
          entry.expiresAt = Math.min(expires, Date.now() + 5 * 60 * 1000); return value;
        } catch { return null; }
        finally { clearTimeout(timeout); lifetime.signal.removeEventListener('abort', abort); }
      })();
      if (runtimeContexts.size >= 32) runtimeContexts.delete(runtimeContexts.keys().next().value);
      runtimeContexts.set(key, entry); return entry.promise;
    }
    async function run(current = readOwner()) {
      if (destroyed || !root.isConnected || !selector.isConnected || !gallery.isConnected || interacting()) return;
      const ticket = ++generation, item = current.selected;
      if (!item || !(item.file instanceof File) || !(item.original instanceof File)) { refreshView(current); return; }
      const bound = { revision: current.revision, contextKey: current.key };
      diagnostic = { ...bound, state: 'pending', message: 'Vérification du fichier effectif à la taille choisie…' }; refreshView(current);
      try {
        const modules = window.TodayMerchProductModules;
        if (modules?.has?.(root, 'diagnostics')) await modules.ensure(root, 'diagnostics');
        if (destroyed || ticket !== generation || interacting()) return;
        const preflight = window.TodayMerchArtworkDesignPreflight;
        const analyzer = window.TodayMerchMidoceanArtworkFeasibility;
        if (!preflight?.evaluate || !analyzer?.analyzeFile) throw fail('diagnostic_unavailable');
        const [originalSha256, effectiveSha256, analysis, measurements, runtimeContext] = await Promise.all([
          memoHash(item.original), memoHash(item.file), memoAnalysis(item.file), memoSvgMeasurements(item.file), runtimeDiagnosticContext(current)]);
        if (destroyed || !root.isConnected || ticket !== generation || interacting()) return;
        const latest = readOwner();
        if (latest.key !== current.key) return;
        // Native public offers can omit supplier product/SKU or expose opaque offer IDs.
        // Missing identities stay unresolved; never derive them from titles or Shopify IDs.
        const context = runtimeContext?.ruleContext || item.ruleContext || { supplier, variantId: current.variantId, support: 'unknown' };
        const measurementController = window.TodayMerchProductDesignMeasurement?.attach(root);
        const physicalOwner = typeof current.owner?.readPhysicalDiagnosticBinding === 'function' ? current.owner : measurementController;
        const previewRecord = selector.getStudioPreviewRecord?.(item.original, item.zoneKey, '', { artworkId: item.artworkId || 'base' });
        const previewMatchesEffective = (item.previewMatchesEffective ??
          selector.getStudioPreviewSource?.(item.original, item.zoneKey, { artworkId: item.artworkId || 'base' }) === item.file)
          && previewRecord?.status === 'ready' && previewRecord.sourceFile === item.file;
        let physical = null;
        if (runtimeContext && typeof physicalOwner?.readPhysicalDiagnosticBinding === 'function') {
          try { physical = await physicalOwner.readPhysicalDiagnosticBinding({ context: runtimeContext, zone: current.zone, artworkId: current.artworkId || 'base',
            variantId: current.variantId, techniqueId: item.techniqueId, revision: current.revision, configurationRevision: current.snapshot.configurationRevision, originalSha256, effectiveSha256, contentFrame: item.contentFrame, measurements, placement: current.transform, previewMatchesEffective }); } catch { /* Missing mapping stays unknown. */ }
        }
        if (destroyed || ticket !== generation || readOwner().key !== current.key || interacting()) return;
        const resolution = rules.resolveTechniqueRules(context);
        const nativePlacement = item.placement && [item.placement.xMm, item.placement.yMm, item.placement.rotationDeg].every(Number.isFinite)
          ? { centerXMm: item.placement.xMm, centerYMm: item.placement.yMm, rotationDeg: item.placement.rotationDeg, revision: current.revision } : null;
        const estimatedProjection = previewMatchesEffective && nativePlacement && current.snapshot.configurationRevision
          && [item.dimensions?.widthMm, item.dimensions?.heightMm].every(value => Number.isFinite(value) && value > 0)
          ? { verified: false, certainty: 'estimate', effectiveSha256, reference: 'visible_content', revision: current.snapshot.configurationRevision,
            method: 'studio_visible_dimensions_estimate' } : null;
        const input = { context, artwork: { originalSha256, effectiveSha256,
          revision: item.artworkRevision, adaptationRevision: item.preparationRevision, previewMatchesEffective }, contentFrame: physical?.contentFrame || item.contentFrame, physicalBinding: runtimeContext?.physicalBinding || null,
          analysis: { ...analysis, sha256: analysis?.sha256 || effectiveSha256 }, measurements: measurements || null,
          dimensionsMm: physical?.dimensionsMm || item.dimensions, placement: physical?.placement || nativePlacement,
          crop: physical?.crop || (current.transform ? { left: current.transform.cropLeft ?? 0, right: current.transform.cropRight ?? 0,
            top: current.transform.cropTop ?? 0, bottom: current.transform.cropBottom ?? 0 } : null),
          projection: physical?.projection || estimatedProjection || { verified: false, effectiveSha256, reference: 'visible_content', revision: current.snapshot.configurationRevision },
          rules: runtimeContext?.rules || rules.toPreflightRules(resolution) };
        const result = preflight.evaluate(input);
        if (result?.binding?.key !== preflight.createBinding(input).key) throw fail('stale');
        if (ticket !== generation || readOwner().key !== current.key) return;
        diagnostic = { ...bound, state: result.status === 'adaptation_required' ? 'blocked' : result.status === 'measured_checks_passed' ? 'checked' : 'attention',
          message: result.findings?.slice(0, 2).map(item => item.message).join(' ') || 'Contrôles disponibles effectués ; vérification humaine requise.',
          customerMessage: result.findings?.filter(item => item.level === 'blocking').slice(0, 2).map(item => item.message).join(' ') || '' };
        const expiresAt = runtimeContext ? runtimeContext.diagnosticContextStatus === 'documentary' ? runtimeContext.diagnosticExpiresAt
          : Math.min(Date.parse(runtimeContext.validUntil), Date.now() + 5 * 60 * 1000) : Infinity;
        clearTimeout(diagnosticExpiryTimer);
        if (Number.isFinite(expiresAt)) diagnosticExpiryTimer = setTimeout(() => schedule(), Math.max(0, expiresAt - Date.now()));
        api.lastDiagnostic = result;
        root.dispatchEvent(new CustomEvent('todaymerch:artwork-compatibility-change', { bubbles: true,
          detail: { zoneKey: current.zoneKey, fingerprint: current.key, revision: current.revision, status: result.status,
            advisory: true, diagnostic: result } }));
        refreshView();
      } catch {
        if (destroyed || ticket !== generation) return;
        diagnostic = { ...bound, state: 'error', message: 'La vérification locale est indisponible. Votre fichier est conservé.',
          customerMessage: 'Vérification du fichier momentanément indisponible. Votre design est conservé.' }; refreshView();
      }
    }
    function cancelScheduled() {
      clearTimeout(timer); clearTimeout(fallback);
      if (frame !== undefined) cancelAnimationFrame(frame);
      timer = fallback = frame = undefined;
    }
    function reconcileScheduled() {
      cancelScheduled();
      if (destroyed || !root.isConnected || !selector.isConnected || !gallery.isConnected || interacting()) return;
      const current = readOwner(), ticket = generation;
      dossier?.onOwnerChange?.(readDossierOwner(current));
      if (ticket !== generation) return;
      replayAppliedPreparation(dossier, current);
      if (ticket !== generation) return;
      preparation?.update?.({ ownerSnapshot: current });
      publishPad(current);
      if (ticket !== generation) return;
      refreshView();
      // Colour preparation, acceptance and dossier identity are necessary for
      // editing. Detailed physical diagnostics run only on explicit request.
      if (pendingDiagnosticRequest) { pendingDiagnosticRequest = false; void run(current); }
    }
    function schedule(options = {}) {
      if (destroyed || padRollback || !root.isConnected) return;
      if (options.type === 'todaymerch:personalization-selection-change') {
        const selectedZone = options.detail?.zoneKey || selector.activeZoneKey;
        const selectedTechnique = options.detail?.techniqueId;
        const observed = padObserved.get(`${padVariant}|${selectedZone}|${gallery.getActiveDesignArtworkId?.(selectedZone) || 'base'}`);
        // Technique transitions carry acceptance/history semantics, including
        // a round trip within one task. A quantity-only selection notification
        // with the same technique does not require a Studio reconstruction.
        if (!selectedTechnique || !observed || JSON.parse(observed)[0] !== selectedTechnique) publishPad(readOwner());
      }
      generation++; diagnostic = null; if (api) api.lastDiagnostic = null;
      clearTimeout(diagnosticExpiryTimer);
      if (interacting()) {
        // Pointer frames only invalidate advisory work. Native snapshots measure
        // every placement; reconcile them after the gesture has settled. Dossier
        // reads and save boundaries still call readOwner for the current identity.
        deferredForGesture = true; cancelScheduled();
        return;
      }
      deferredForGesture = false;
      if (frame !== undefined || timer !== undefined) return;
      // rAF precedes paint. The task queued from it lets the committed price
      // paint before reading the complete Studio for advisory presentation.
      frame = requestAnimationFrame(() => { frame = undefined; timer = setTimeout(reconcileScheduled, 0); });
      fallback = setTimeout(reconcileScheduled, 200);
    }
    preparation = window.TodayMerchProductImagePreparation?.create({ root, mount, notesMount: card.preparationNotes, selector, readOwner, readApplicationOwner,
      onCancel: cancelPadDraft, onDraftChange: onPadDraft, onCommitted: acceptPad });
    const on = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal: lifetime.signal });
    for (const name of events) on(root, name, schedule);
    on(root, 'todaymerch:artwork-treatment-change', () => { publishPad(); refreshView(); });
    on(root, 'todaymerch:artwork-source-copied', event => dossier?.copied?.(event.detail));
    on(root, 'todaymerch:artwork-adaptation-accepted', event => {
      const item = readDossierOwner().selected;
      if (item && item.original === event.detail?.originalFile && item.file === event.detail?.effectiveFile) dossier?.accepted?.(event.detail);
      refreshView();
    });
    on(root, 'todaymerch:artwork-preparation-applied', event => {
      const current = readOwner(), item = readDossierOwner(current).selected;
      if (current.selected && event.detail?.effectiveFile === current.selected.file && event.detail.originalFile === current.selected.original
        && automaticBinding(event.detail.receipt, current, current.selected, isPad(current, current.selected))) {
        technicalApplications.set(padKey(current), event.detail.receipt);
      }
      if (item && item.original === event.detail?.originalFile && item.file === event.detail?.effectiveFile) dossier?.applied?.(event.detail);
      refreshView();
    });
    on(root, 'click', event => {
      // Retain the previous artwork/technique for reversible preparation.
      // A quantity click cannot change that history and needs no snapshot.
      if (selector.contains(event.target) || gallery.contains(event.target)) reconcilePad(readOwner());
    }, { capture: true });
    on(root, 'change', schedule); on(root, 'input', event => { if (event.target.matches?.('input[name="quantity"]')) schedule(); });
    for (const name of ['pointerup', 'pointercancel', 'blur']) {
      // Gallery end handlers can run later in this event's propagation.
      on(window, name, () => {
        if (!deferredForGesture) return;
        if (!interacting()) schedule();
        clearTimeout(gestureEndTimer);
        gestureEndTimer = setTimeout(() => {
          if (deferredForGesture && !interacting()) schedule();
        }, 0);
      });
    }
    on(root, 'todaymerch:preview-effects-render', event => {
      const current = readOwner(), item = current.selected, detail = event.detail;
      if (!item || String(detail?.variantId) !== String(current.variantId)
        || detail?.zoneKey !== current.zoneKey || detail?.techniqueId !== item.techniqueId) return;
      const applied = detail.applied || [], reasons = [
        ...(detail.stages || []).map(stage => stage.reason || ''),
        ...(detail.fallbacks || []).map(item => typeof item === 'string' ? item : item.reason),
      ];
      previewBinding = current.key;
      previewMessage = applied.includes('patch') && detail.simulationReference === true && detail.previewNotice
        ? detail.previewNotice
        : reasons.includes('patch-envelope-outside-zone')
          ? 'Le support de l’écusson dépasserait la surface disponible. Réduisez ou recentrez le visuel pour afficher sa simulation.'
        : applied.includes('engraving')
          ? 'Simulation de gravure. Teinte indicative selon le support.'
        : applied.includes('embroidery')
          ? (detail.stages || []).some(stage => stage.name === 'embroidery' && stage.process === 'embroidery_3d')
            ? 'Simulation de broderie 3D. Fils, relief et couleurs indicatifs.'
            : 'Simulation de broderie. Fils et couleurs indicatifs.'
        : applied.some(name => name !== 'curvature')
          ? 'Aperçu indicatif du marquage.'
        : reasons.includes('embroidery_requires_flat_colors')
          ? 'Aperçu du placement. La texture brodée nécessite un visuel analysé en couleurs unies.'
        : reasons.some(reason => /engraving[_-]/.test(reason))
          ? 'Aperçu du placement. L’effet de gravure n’est pas disponible pour ce support.'
        : reasons.some(reason => /patch[_-]|backing[_-]/.test(reason))
          ? 'Aperçu du placement de l’écusson. Support indicatif.'
          : 'Aperçu du placement. Finition non simulée.';
      refreshView();
    });
    api = { read, readOwner, execute, readPadAcceptance: () => preparationState(), readPreparationAcceptance: () => preparationState(), canConfirm: () => preparationState().canConfirm,
      settleAutomaticPreparation: options => preparation?.settleAutomaticPreparation?.({ ...options, signal: options?.signal || lifetime.signal })
        || Promise.resolve({ status: 'unavailable', productionReady: false }),
      cancelPadDraft, focusPadPreparation, refresh: schedule, connectDossier, lastDiagnostic: null, dossier, destroy() {
      if (destroyed) return; destroyed = true; generation++; padSerial++; padAccepted.clear(); padHistory.clear(); padEditors.clear(); delete root.dataset.padAcceptance; cancelScheduled(); clearTimeout(gestureEndTimer); clearTimeout(diagnosticExpiryTimer); lifetime.abort(); dossier?.destroy?.(); preparation?.destroy?.(); mount.remove(); instances.delete(root); informationCards.delete(root); mountedRoots.delete(root); api.lastDiagnostic = null;
    } };
    instances.set(root, api); mountedRoots.add(root);
    on(document, 'shopify:section:unload', event => { if (event.target === root || event.target.contains?.(root)) { unloaded.add(root); api.destroy(); } });
    connectDossier(); publishPad(readOwner()); schedule();
    root.dispatchEvent(new CustomEvent('todaymerch:personalization-orchestrator-ready', { bubbles: true, detail: { api } }));
    return api;
  }
  function boot() {
    for (const root of mountedRoots) if (!root.isConnected) instances.get(root)?.destroy();
    for (const root of document.querySelectorAll('product-info')) attach(root);
  }
  function discoverProductRoots(records) {
    const roots = new Set();
    let removed = false;
    for (const record of records) {
      const owner = record.target.closest?.('product-info');
      if (owner && !instances.has(owner)) roots.add(owner);
      if (record.type === 'attributes') continue;
      if (record.removedNodes.length) removed = true;
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches('product-info')) roots.add(node);
        for (const root of node.querySelectorAll('product-info')) roots.add(root);
      }
    }
    if (removed) for (const root of mountedRoots) if (!root.isConnected) instances.get(root)?.destroy();
    for (const root of roots) attach(root);
  }
  window.TodayMerchPersonalizationOrchestrator = Object.freeze({ attach, get: root => instances.get(root) || pendingApis.get(root), boot,
    registerDossierFactory(factory) {
      if (!factory || typeof factory.create !== 'function') throw new TypeError('A dossier factory is required.');
      dossierFactory = factory;
      for (const root of mountedRoots) instances.get(root)?.connectDossier();
      boot();
    } });
  boot();
  const observer = new MutationObserver(discoverProductRoots);
  observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['data-xd-v2-ready', 'data-personalization-v2-binding', 'data-personalization-streamlined-active'] });
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('DOMContentLoaded', boot, { once: true });
  // Native deferred aliases can finish after their owner has already become ready.
  // Retry after script execution without loading or replacing any native module.
  document.addEventListener('load', event => { if (event.target?.tagName === 'SCRIPT') boot(); }, true);
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    for (const root of waitingRoots) { unloaded.add(root); preparationListeners.get(root)?.abort(); }
    waitingRoots.clear();
    for (const root of [...mountedRoots]) instances.get(root)?.destroy();
  }, { once: true });
})();
